import { useCallback, useRef, useState } from 'react'
import { toast } from 'sonner'

import type { Product } from '@/lib/api/product-api'
import { productApi } from '@/lib/api/product-api'
import { looksLikeUrl } from '@/lib/scan-code'
import { m } from '@/lib/paraglide/messages'

interface UseBarcodeOptions {
    onProductFound: (product: Product, qty: number) => void
    onBarcodeNotFound: (barcode: string) => void
}

interface UseBarcodeReturn {
    entryMode: 'quick' | 'normal'
    setEntryMode: (mode: 'quick' | 'normal') => void
    toggleEntryMode: () => void
    scanQuantity: number
    setScanQuantity: (qty: number) => void
    handleSearchKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void
    /** Force a barcode lookup bypassing the keystroke-timing heuristic. */
    scanBarcode: (barcode: string) => void
}

// Keystroke-timing heuristic for USB HID scanners. Loose enough to catch
// slower consumer scanners (~150-200ms/char) without matching human typing.
const SCAN_WINDOW_MS = 800
const SCAN_MIN_KEYSTROKES = 3

/**
 * Shared hook for USB HID barcode scanner detection + product lookup.
 * Detects rapid keystrokes as scanner input; callers can also force a lookup
 * via `scanBarcode` when the heuristic misses.
 */
export function useBarcodeScanner({
    onProductFound,
    onBarcodeNotFound,
}: UseBarcodeOptions): UseBarcodeReturn {
    const [entryMode, setEntryMode] = useState<'quick' | 'normal'>('quick')
    const [scanQuantity, setScanQuantity] = useState(1)
    const keystrokeTimesRef = useRef<number[]>([])

    const handleBarcodeScan = useCallback(
        async (barcode: string) => {
            const trimmed = barcode.trim()
            if (!trimmed) return
            // A URL is never a product barcode — a scanned delivery-note QR or
            // in-app browser link decodes to a full URL. Skip the lookup (it
            // only 404s) and don't open the create-product dialog for it.
            if (looksLikeUrl(trimmed)) {
                toast.error(m.scan_url_not_product())
                return
            }
            try {
                const resp = await productApi.getByBarcode(trimmed)
                if (resp.data) {
                    onProductFound(resp.data, scanQuantity)
                    toast.success(m.pos_barcode_added({ name: resp.data.name }))
                    setScanQuantity(1)
                    return
                }
                onBarcodeNotFound(trimmed)
            } catch {
                onBarcodeNotFound(trimmed)
            }
        },
        [onProductFound, onBarcodeNotFound, scanQuantity],
    )

    // Detect USB HID barcode scanner input (rapid keystrokes + Enter)
    const handleSearchKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            const now = Date.now()
            if (e.key !== 'Enter') {
                keystrokeTimesRef.current.push(now)
                keystrokeTimesRef.current = keystrokeTimesRef.current.filter(
                    (t) => now - t < SCAN_WINDOW_MS,
                )
                return
            }

            const value = (e.target as HTMLInputElement).value.trim()
            if (!value) return

            const recentCount = keystrokeTimesRef.current.filter(
                (t) => now - t < SCAN_WINDOW_MS,
            ).length
            keystrokeTimesRef.current = []

            if (recentCount >= SCAN_MIN_KEYSTROKES) {
                e.preventDefault()
                e.nativeEvent.stopImmediatePropagation()
                void handleBarcodeScan(value)
                return
            }
            // Slow typing: let event propagate. Caller decides whether to
            // fall back to scanBarcode when dropdown search is empty.
        },
        [handleBarcodeScan],
    )

    const toggleEntryMode = useCallback(() => {
        setEntryMode((prev) => {
            const next = prev === 'quick' ? 'normal' : 'quick'
            if (next === 'quick') setScanQuantity(1)
            return next
        })
    }, [])

    return {
        entryMode,
        setEntryMode,
        toggleEntryMode,
        scanQuantity,
        setScanQuantity,
        handleSearchKeyDown,
        scanBarcode: handleBarcodeScan,
    }
}
